import { useEffect, useMemo, useState } from 'react';
import { format } from 'date-fns';
import { fetchTimeEntries, type TimeEntry } from '@/db/timeEntries';
import { handleError } from '@/utils/errorHandler';

export interface WeeklyTime {
  entries: TimeEntry[];
  /** Millisecondi registrati per giorno, chiave `yyyy-MM-dd`. */
  byDay: Map<string, number>;
  /** Millisecondi registrati per task. */
  byTask: Map<string, number>;
  /** Millisecondi registrati per persona. */
  byUser: Map<string, number>;
  total: number;
  loading: boolean;
}

const EMPTY: WeeklyTime = {
  entries: [],
  byDay: new Map(),
  byTask: new Map(),
  byUser: new Map(),
  total: 0,
  loading: false,
};

/**
 * Ore registrate in una settimana.
 *
 * Il timesheet mostrava zero ovunque perché leggeva `timeSpent` sui task, un
 * campo che non viene più aggiornato: le ore vivono nella tabella
 * `time_entries`. Le policy RLS restringono già la lettura ai task delle
 * organizzazioni di cui si fa parte, quindi qui non serve filtrare per utente
 * — la vista Team ha bisogno di vedere anche gli altri.
 */
export function useWeeklyTimeEntries(weekStart: Date, weekEnd: Date): WeeklyTime {
  const startDate = format(weekStart, 'yyyy-MM-dd');
  const endDate = format(weekEnd, 'yyyy-MM-dd');

  const [entries, setEntries] = useState<TimeEntry[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);

    fetchTimeEntries({ startDate, endDate })
      .then((rows) => {
        if (!cancelled) setEntries(rows);
      })
      .catch((err) => {
        if (cancelled) return;
        handleError(err, {
          level: 'warning',
          message: 'Impossibile caricare le ore registrate della settimana',
          operation: 'fetchTimeEntries',
        });
        setEntries([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => { cancelled = true; };
  }, [startDate, endDate]);

  return useMemo(() => {
    if (entries.length === 0) return { ...EMPTY, loading };

    const byDay = new Map<string, number>();
    const byTask = new Map<string, number>();
    const byUser = new Map<string, number>();
    let total = 0;

    for (const entry of entries) {
      byDay.set(entry.date, (byDay.get(entry.date) || 0) + entry.duration);
      byTask.set(entry.taskId, (byTask.get(entry.taskId) || 0) + entry.duration);
      byUser.set(entry.userId, (byUser.get(entry.userId) || 0) + entry.duration);
      total += entry.duration;
    }

    return { entries, byDay, byTask, byUser, total, loading };
  }, [entries, loading]);
}
